import { motion } from "framer-motion";
import { Building2, Contact2, Phone, Search } from "lucide-react";
import { Button } from "../../../../../components/ui/button";
import { Card, CardContent } from "../../../../../components/ui/card";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { FieldErrors } from "../hooks/use-saldo-consulta";
import { SearchFormData } from "../types";

interface SearchFormProps {
  searchForm: SearchFormData;
  onFieldChange: (field: keyof SearchFormData, value: string) => void;
  onSearch: () => void;
  isSearching: boolean;
  fundName: string;
  fieldErrors: FieldErrors;
}

export function SearchForm({
  searchForm,
  onFieldChange,
  onSearch,
  isSearching,
  fundName,
  fieldErrors,
}: SearchFormProps) {
  return (
    <Card className="border-2 hover:border-primary/30 transition-colors">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4">
          <motion.div
            className="h-1 w-8 bg-gradient-to-r from-primary to-secondary rounded-full"
            initial={{ width: 0 }}
            animate={{ width: 32 }}
            transition={{ duration: 0.5 }}
          />
          <h2 className="text-xl font-bold text-foreground">
            Dados para Consulta
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Label htmlFor="telefone">Telefone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="telefone"
                placeholder="+55 (11) 99999-9999"
                value={searchForm.telefone}
                onChange={(e) => onFieldChange("telefone", e.target.value)}
                className={`pl-9 transition-all ${
                  fieldErrors.telefone ? "border-destructive" : "focus:border-primary"
                }`}
              />
            </div>
            {fieldErrors.telefone && (
              <motion.p
                className="text-sm text-destructive"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {fieldErrors.telefone}
              </motion.p>
            )}
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Label htmlFor="cpf">CPF</Label>
            <div className="relative">
              <Contact2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="cpf"
                placeholder="000.000.000-00"
                value={searchForm.cpf}
                onChange={(e) => onFieldChange("cpf", e.target.value)}
                className={`pl-9 transition-all ${
                  fieldErrors.cpf ? "border-destructive" : "focus:border-primary"
                }`}
              />
            </div>
            {fieldErrors.cpf && (
              <motion.p
                className="text-sm text-destructive"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {fieldErrors.cpf}
              </motion.p>
            )}
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Label htmlFor="cnpj">CNPJ</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="cnpj"
                placeholder="00.000.000/0000-00"
                value={searchForm.cnpj}
                onChange={(e) => onFieldChange("cnpj", e.target.value)}
                className={`pl-9 transition-all ${
                  fieldErrors.cnpj ? "border-destructive" : "focus:border-primary"
                }`}
              />
            </div>
            {fieldErrors.cnpj && (
              <motion.p
                className="text-sm text-destructive"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {fieldErrors.cnpj}
              </motion.p>
            )}
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Label className="invisible">Buscar</Label>
            <Button
              onClick={onSearch}
              disabled={isSearching || !fundName}
              className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              {isSearching ? (
                <motion.div
                  className="flex items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                  Consultando...
                </motion.div>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Consultar Saldo
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
}
